import { Box, Button, makeStyles, Popover, Typography } from '@material-ui/core'
import Icon from '@mdi/react'
import {
  mdiCalendarOutline,
  mdiCashMultiple,
  mdiClockOutline,
  mdiEarth,
  mdiTranslate,
} from '@mdi/js'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import colors from '../../../assets/colors'

// Mesmos países do CurrencyCountrySeeder da API. Desenho simplificado,
// pensado para ficar legível em ~22px de largura
const FLAGS = {
  BR: (
    <>
      <rect width="20" height="14" fill="#009C3B" />
      <polygon points="10,1.6 18.4,7 10,12.4 1.6,7" fill="#FFDF00" />
      <circle cx="10" cy="7" r="3.4" fill="#002776" />
      <path
        d="M6.7 6.3 Q10 5.4 13.3 7.6"
        stroke="#FFF"
        strokeWidth="0.7"
        fill="none"
      />
    </>
  ),
  PY: (
    <>
      <rect width="20" height="14" fill="#FFF" />
      <rect width="20" height="4.67" fill="#D52B1E" />
      <rect y="9.33" width="20" height="4.67" fill="#0038A8" />
      <circle
        cx="10"
        cy="7"
        r="1.6"
        fill="none"
        stroke="#7A9A3A"
        strokeWidth="0.5"
      />
    </>
  ),
  ES: (
    <>
      <rect width="20" height="14" fill="#AA151B" />
      <rect y="3.5" width="20" height="7" fill="#F1BF00" />
    </>
  ),
  US: (
    <>
      <rect width="20" height="14" fill="#FFF" />
      {[0, 2, 4, 6, 8, 10, 12].map(i => (
        <rect
          key={i}
          y={(i * 14) / 13}
          width="20"
          height={14 / 13}
          fill="#B22234"
        />
      ))}
      <rect width="8" height="7.54" fill="#3C3B6E" />
    </>
  ),
}

// Gentílico usado na frase "se comportar como ..."
const DEMONYMS = {
  BR: 'brasileiro',
  PY: 'paraguaio',
  ES: 'espanhol',
  US: 'americano',
}

// Espelha config/i18n.php (supported_locales) da API
const LOCALE_NAMES = {
  pt_BR: 'Português (Brasil)',
  es_PY: 'Español (Paraguay)',
  es_ES: 'Español (España)',
  en_US: 'English (United States)',
}

const useStyles = makeStyles({
  flagButton: {
    minWidth: 0,
    height: '32px',
    padding: '0 10px',
    marginLeft: '4px',
    borderRadius: '8px',
    border: `1px solid ${colors.grayMedium}`,
    backgroundColor: colors.graylight,
    '&:hover': {
      backgroundColor: colors.lightGray,
    },
  },
  flag: {
    display: 'block',
    flexShrink: 0,
    borderRadius: 2,
    boxShadow: '0 0 0 1px rgba(0, 11, 65, 0.12)',
  },
  flagLarge: {
    boxShadow: '0 0 0 1px rgba(0, 11, 65, 0.12)',
    borderRadius: 4,
  },
  fallbackFlag: {
    color: colors.primary,
  },
  // Popover no padrão do LaundryAlertsBell
  paper: {
    width: 360,
    maxWidth: 'calc(100vw - 32px)',
    marginTop: 8,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0, 11, 65, 0.16)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '16px 16px 12px',
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.primary,
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: 12,
    color: colors.textPrimary,
  },
  lead: {
    margin: '0 16px 12px',
    padding: 12,
    borderRadius: 10,
    backgroundColor: colors.lightGray,
    fontSize: 13,
    lineHeight: 1.5,
    color: colors.textGray,
    '& strong': {
      color: colors.primary,
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 16px 6px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    '& + &': {
      borderTop: `1px solid ${colors.graylight}`,
    },
  },
  rowIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: colors.lightGray,
    color: colors.primary,
  },
  rowInfo: {
    flex: 1,
    minWidth: 0,
  },
  rowLabel: {
    fontSize: 12,
    color: colors.textPrimary,
  },
  rowValue: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.primary,
    wordBreak: 'break-word',
  },
  rowHint: {
    fontSize: 12,
    color: colors.textGray,
  },
})

// Intl espera "pt-BR"; a API guarda "pt_BR"
const toBcp47 = locale => (locale || 'pt_BR').replace('_', '-')

// Intl lança RangeError com fuso/moeda/locale inválidos: nesses casos
// o campo some do popup em vez de derrubar o header
const safe = fn => {
  try {
    return fn()
  } catch (e) {
    return null
  }
}

export const Flag = ({ code, width, className }) => {
  const classes = useStyles()
  const flag = FLAGS[code]

  if (!flag) {
    return (
      <Icon
        path={mdiEarth}
        size={width > 30 ? 1.4 : 0.75}
        className={className || classes.fallbackFlag}
      />
    )
  }

  return (
    <svg
      viewBox="0 0 20 14"
      width={width}
      height={(width * 14) / 20}
      preserveAspectRatio="none"
      className={`${classes.flag} ${className || ''}`}
      aria-hidden="true"
    >
      {flag}
    </svg>
  )
}

const TenantLocaleFlag = () => {
  const classes = useStyles()
  const tenantLocale = useSelector(state => state.auth.tenantLocale)

  const [anchorEl, setAnchorEl] = useState(null)

  const { country, locale, timezone, currency } = tenantLocale || {}

  // Recalcula ao abrir para o horário de exemplo não ficar parado
  const details = useMemo(() => {
    if (!tenantLocale) return null

    const bcp47 = toBcp47(locale)
    const now = new Date()

    return {
      localeName: LOCALE_NAMES[locale] || locale,
      timezoneName: safe(
        () =>
          new Intl.DateTimeFormat('pt-BR', {
            timeZone: timezone,
            timeZoneName: 'long',
          })
            .formatToParts(now)
            .find(part => part.type === 'timeZoneName')?.value
      ),
      currentTime: safe(() =>
        new Intl.DateTimeFormat(bcp47, {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
        }).format(now)
      ),
      dateExample: safe(() =>
        new Intl.DateTimeFormat(bcp47, {
          timeZone: timezone,
          dateStyle: 'short',
        }).format(now)
      ),
      currencyName: safe(() =>
        new Intl.DisplayNames(['pt-BR'], { type: 'currency' }).of(
          currency?.code
        )
      ),
      moneyExample: safe(() =>
        new Intl.NumberFormat(bcp47, {
          style: 'currency',
          currency: currency?.code,
        }).format(1234.5)
      ),
    }
  }, [tenantLocale, anchorEl])

  // Administrador não tem lavanderia, então não há país para mostrar
  if (!tenantLocale) return null

  const demonym = DEMONYMS[country.code]

  const rows = [
    {
      icon: mdiTranslate,
      label: 'Idioma',
      value: details.localeName,
    },
    // {
    //   icon: mdiClockOutline,
    //   label: 'Fuso horário',
    //   value: details.timezoneName || timezone,
    //   hint: details.currentTime
    //     ? `Agora são ${details.currentTime} (${timezone})`
    //     : timezone,
    // },
    // {
    //   icon: mdiCalendarOutline,
    //   label: 'Formato de data',
    //   value: details.dateExample,
    //   hint: 'Exemplo com a data de hoje',
    // },
    // {
    //   icon: mdiCashMultiple,
    //   label: 'Moeda',
    //   value: currency
    //     ? `${details.currencyName || currency.code} (${currency.symbol})`
    //     : null,
    //   hint: details.moneyExample
    //     ? `Valores aparecem como ${details.moneyExample}`
    //     : null,
    // },
  ].filter(row => row.value)

  return (
    <>
      <Button
        className={classes.flagButton}
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-label={`Padrão regional: ${country.name}`}
        title={country.name}
      >
        <Flag code={country.code} width={22} />
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.paper }}
      >
        <Box className={classes.header}>
          <Flag code={country.code} width={40} className={classes.flagLarge} />
          <Box>
            <Typography className={classes.title}>{country.name}</Typography>
            <Typography className={classes.subtitle}>
              Padrão regional da sua lavanderia
            </Typography>
          </Box>
        </Box>

        <Typography component="div" className={classes.lead}>
          Seu sistema está configurado para se comportar como{' '}
          <strong>{demonym || `no padrão de ${country.name}`}</strong>: datas,
          horários, valores e endereços seguem o formato usado{' '}
          {demonym ? `no país` : 'lá'}.
        </Typography>

        <Box className={classes.list}>
          {rows.map(row => (
            <Box key={row.label} className={classes.row}>
              <Box className={classes.rowIcon}>
                <Icon path={row.icon} size={0.75} />
              </Box>
              <Box className={classes.rowInfo}>
                <Typography className={classes.rowLabel}>
                  {row.label}
                </Typography>
                <Typography className={classes.rowValue}>
                  {row.value}
                </Typography>
                {row.hint && (
                  <Typography className={classes.rowHint}>
                    {row.hint}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

export default TenantLocaleFlag
